import { takeLatest, put, call, select, all } from "redux-saga/effects";
import { generatePath } from "react-router-dom";
import { saveAs } from "file-saver";
import { Intent } from "@blueprintjs/core";
import Toaster, { toastIcons } from "../../components/Page/components/Toaster";
import BackendClient from "../../api/backend-client";
import { ERROR_CODE_BAD_REQUEST, ErrorType } from "api/api-error";
import { PointScalingApiMethods } from "api/api-methods";
import RoutePaths from "../../routes/route-paths";
import { getCurrentDateTimeStamp } from "utils/dateTime";
import logger from "../../utils/logger";
import { waitForLogin } from "../auth/auth.saga";
import { decisionTreeSelector } from "../global/global.selectors";
import { resetDecisionTreeSelectedSteps, setDecisionTreeCurrentStep, setDecisionTreeModalOpen, setDecisionTreePreviousStep, } from "../global/global.actions";
import { setWorkspaceServerError } from "../ps-workspace/ps-workspace.actions";
import { loadWorkspacesListFailure, loadWorkspacesListSuccessful, createTagsSuccessful, createTagsFailure, setDuplicatedWorkspaceId, duplicateWorkspaceSuccessful, duplicateWorkspaceFailure, exportWorkspaceSuccessful, exportWorkspaceFailure, validateWorkspaceForImportSuccessful, validateWorkspaceForImportFailure, importWorkspaceSuccessful, importWorkspaceFailure, resetValidatedWorkspaceForImport, } from "./ps-workspace-list.actions";
import { PsWorkspaceListActionType } from "./ps-workspace-list.actions";
import { apiErrorHandler } from "store/global/global.saga";
import { navigate } from "shared/navigation";
export function* loadWorkspacesSaga() {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const data = yield call([backendClient, PointScalingApiMethods.GetPsWorkspacesList]) ||
            [];
        yield put(loadWorkspacesListSuccessful(data));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "workspaces load failed");
        yield put(loadWorkspacesListFailure());
    }
}
export function* createTagsSaga({ payload }) {
    try {
        const backendClient = BackendClient.getInstance();
        yield all(payload.map((tag) => call([backendClient, PointScalingApiMethods.CreatePsTag], tag)));
        yield put(createTagsSuccessful());
    }
    catch (e) {
        yield call(apiErrorHandler, e, "tags creation failed");
        yield put(createTagsFailure(e));
        throw e;
    }
}
function* clearCreateWorkspaceFormSaga() {
    try {
        const decisionTree = yield select(decisionTreeSelector);
        yield put(setDecisionTreeModalOpen(false));
        yield put(resetDecisionTreeSelectedSteps());
        yield put(setDecisionTreeCurrentStep(decisionTree));
        yield put(setDecisionTreePreviousStep(null));
        yield put(setDuplicatedWorkspaceId(null));
        yield put(resetValidatedWorkspaceForImport());
    }
    catch (e) {
        logger.error("Clear create workspace form failed");
    }
}
export function* duplicateWorkspaceSaga({ payload }) {
    try {
        const backendClient = BackendClient.getInstance();
        const data = yield call([backendClient, PointScalingApiMethods.DuplicatePsWorkspace], payload);
        yield put(duplicateWorkspaceSuccessful());
        Toaster().show({
            message: "Workspace has successfully been duplicated",
            intent: Intent.SUCCESS,
            icon: toastIcons.info,
        });
        yield call(clearCreateWorkspaceFormSaga);
        navigate(generatePath(RoutePaths.PointScalingWorkspace, { id: data.workspaceId }));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "workspaces duplicate failed");
        yield put(setWorkspaceServerError(e));
        yield put(duplicateWorkspaceFailure(e));
    }
}
export function* validateWorkspaceForImportSaga({ payload }) {
    try {
        const backendClient = BackendClient.getInstance();
        const data = yield call([backendClient, PointScalingApiMethods.ValidatePsWorkspaceForImport], payload.formData);
        if (!data.workspace || !data.name) {
            const error = {
                status: ERROR_CODE_BAD_REQUEST,
                data: {
                    errors: [
                        {
                            text: `${payload.filename} is not a valid Workspace file`,
                            propertyName: null,
                            errorType: ErrorType.Validation,
                        },
                    ],
                },
            };
            throw error;
        }
        yield put(validateWorkspaceForImportSuccessful(data));
        yield put(setDecisionTreeModalOpen(true));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "workspace validation for import failed");
        yield put(setWorkspaceServerError(e));
        yield put(validateWorkspaceForImportFailure(e));
    }
}
export function* importWorkspaceSaga({ payload }) {
    try {
        const backendClient = BackendClient.getInstance();
        const data = yield call([backendClient, PointScalingApiMethods.ImportPsWorkspace], payload);
        yield put(importWorkspaceSuccessful());
        yield put(setDecisionTreeModalOpen(false));
        Toaster().show({
            message: "Workspace has successfully been imported",
            intent: Intent.SUCCESS,
            icon: toastIcons.info,
        });
        yield call(clearCreateWorkspaceFormSaga);
        navigate(generatePath(RoutePaths.PointScalingWorkspace, { id: data.workspaceId }));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "workspace import failed");
        yield put(setWorkspaceServerError(e));
        yield put(importWorkspaceFailure(e));
    }
}
export function* exportWorkspaceFileSaga({ payload }) {
    try {
        const backendClient = BackendClient.getInstance();
        const workspace = yield call([backendClient, PointScalingApiMethods.ExportPsWorkspace], payload);
        yield put(exportWorkspaceSuccessful());
        if (!workspace) {
            throw new Error();
        }
        const json = JSON.stringify(workspace);
        const fileToSave = new Blob([json], { type: "json" });
        saveAs(fileToSave, `${getCurrentDateTimeStamp()}_${workspace.name}.json`);
    }
    catch (e) {
        yield call(apiErrorHandler, e, "export results failed");
        yield put(exportWorkspaceFailure());
    }
}
export default function* psWorkspaceListSaga() {
    yield takeLatest(PsWorkspaceListActionType.LOAD_LIST, loadWorkspacesSaga);
    yield takeLatest(PsWorkspaceListActionType.CLEAR_CREATE_WORKSPACE_FORM, clearCreateWorkspaceFormSaga);
    yield takeLatest(PsWorkspaceListActionType.CREATE_TAGS, createTagsSaga);
    yield takeLatest(PsWorkspaceListActionType.WORKSPACE_DUPLICATE, duplicateWorkspaceSaga);
    yield takeLatest(PsWorkspaceListActionType.WORKSPACE_EXPORT, exportWorkspaceFileSaga);
    yield takeLatest(PsWorkspaceListActionType.WORKSPACE_VALIDATE_FOR_IMPORT, validateWorkspaceForImportSaga);
    yield takeLatest(PsWorkspaceListActionType.WORKSPACE_IMPORT, importWorkspaceSaga);
}
